import { marked, MarkedExtension } from 'marked';
import { smartypants } from 'smartypants';

import { customMarkdownToHTML } from './customMarkdownToHTML';
import URLUtils from './urls/URLUtils';

import { stripAllHtmlTags } from '../../../utils/html';
import { logger } from '../../../utils/logger';

type MarkedExtensionRenderer = MarkedExtension['renderer'];

const linkRenderer: MarkedExtensionRenderer = {
  link(href, title, text) {
    if (!URLUtils.isMail(URLUtils.normaliseMail(href))) {
      href = URLUtils.normalizeUrl(href);
    } else {
      href = URLUtils.normaliseMail(href);
    }

    const link = marked.Renderer.prototype.link.call(this, href, title, text);
    return link.replace('<a', "<a target='_blank' ");
  },
};

marked.use({
  renderer: linkRenderer,
});

const preConvertText = (content: string): string => {
  // TODO: remove CTA button format from content before convert by marked
  // Currently, we use CTA button on Registration page.
  content = content.replace(/B\[([^\]]+)\]\(([^\)]*)\)\(([^\)]+)\)/g, '');
  // Remove URL of anchor link
  content = content.replace(/\[([^\]]+)\]\(([^\)]+)\)/g, (_match: string, p1: string) => p1);
  // Remove images format
  content = content.replace(/!\[\]\(([^\)]+)\)/g, '');

  return stripAllHtmlTags(content);
};

const preConvertMarked = (content: string): string => {
  // TODO: remove CTA button format from content before convert by marked
  // Currently, we use CTA button on Registration page.
  content = content.replace(/B\[([^\]]+)\]\(([^\)]*)\)\(([^\)]+)\)/g, '');

  // Using `smartypants` instead of `marked-smartypants`
  // `marked-smartypants` only work with `marked.parse(<string>) and not work with `marked.parser(<tokens>)`
  content = smartypants(content);

  return stripAllHtmlTags(content);
};

export const decodeMarkdownCharacters = (content: string | undefined | null): string => {
  if (!content) return '';

  return content
    .replace(/(%5C)([_+*`])(?!\2)/g, (_match: string, _p1: string, p2: string) => `\\${p2}`) // TODO: Decode`%5C` to `\` before some special characters (_+*`)
    .replace(/(\\)([_+*`])(?!\2)/g, (_match: string, _p1: string, p2: string) => p2); // TODO: Remove `\` before some special characters
};

export const markdownInlineToHTML = (content: string): string => {
  return customMarkdownToHTML(content);
};

export const replaceLinkByAnchor = (content: string): string => {
  // combine 2 regex to find all urls in content
  // https://stackoverflow.com/questions/3809401/what-is-a-good-regular-expression-to-match-a-url
  // https://stackoverflow.com/questions/35553751/javascript-regex-find-all-urls-outside-a-tags-nested-tags
  const regex =
    /((http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*))(?![^<>]*>|[^"]*?<\/a)/g;
  return content.replace(regex, (_match, capture) => {
    const url =
      capture.startsWith('http') || capture.startsWith('https') ? capture : `https://${capture}`;
    return `<a href="${url}">${capture}</a>`;
  });
};

const emojiRegex = /(\p{Extended_Pictographic})/gu;
const ewebinarEmojiRegex = /ewEmoji(\p{Extended_Pictographic})ewEmoji/gu;
export const markdownToHTML = (content: string): string => {
  try {
    // There is an issue causing a crash when parsing markdown on Safari.
    // It is when there is a specific emoji at the end of a bold block
    // For example: **Yellow 🟡** or __Yellow 🟡__
    // To bypass it try to wrap the emoji with some text before parsing then remove the wrapper later.
    const preconvert = preConvertMarked(content).replace(emojiRegex, 'ewEmoji$1ewEmoji');

    const lexer = new marked.Lexer({ gfm: true });

    (lexer as any).tokenizer.rules.inline.url =
      /^(((?:ftp|http(s)?):\/\/|www\.)?(?:[a-zA-Z0-9\-]+\.[a-zA-Z]{1,6})+[^<\s]*)|^email/;
    (lexer as any).tokenizer.rules.inline.text =
      /^(`+|[^`])(?:[\s\S]*?(?:(?=[\\<!\[`*~]|\b_|https?:\/\/|ftp:\/\/|www\.|(?:[a-zA-Z0-9\-]+\.[a-zA-Z]{1,6})|$)|(?:[a-zA-Z0-9\-]+\.?)+[^\s<]*|[^ ](?= {2,}\n)|[^a-zA-Z0-9.!#$%&'*+\/=?_`{\|}~-](?=[a-zA-Z0-9.!#$%&'*+\/=?_`{\|}~-]+@))|(?= {2,}\n|[a-zA-Z0-9.!#$%&'*+\/=?_`{\|}~-]+@))/;

    const tokens = lexer.lex(preconvert);
    const html = marked.parser(tokens).replace(ewebinarEmojiRegex, '$1');

    return customMarkdownToHTML(html);
  } catch (error) {
    logger.error('markdownToHTML', `markdownToHTML -> ERROR: ${error} -> content: ${content}`);
    return content;
  }
};

export const markdownToText = (content?: string | null): string => {
  if (!content) return '';
  content = preConvertText(content);
  return stripAllHtmlTags(markdownToHTML(content));
};

export const isMarkdown = (content: string): boolean => {
  return content.includes('*') || content.includes('**') || content.includes('[');
};
